import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sprout, CheckCircle, Heart, Users, ArrowRight, Leaf, Sun, Droplets, Mountain } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function AgriculturePage() {
  return (
    <div data-testid="agriculture-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-white/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Sprout className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
              Agriculture & Rural Development
            </h1>
            <p className="text-xl text-white/90">
              Strengthening livelihoods, enhancing food security, and promoting sustainable growth
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="relative py-12 overflow-hidden bg-[#FDF8F4]">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_fa85df46-347d-469f-8cb9-d6bc46d2cb89/artifacts/0wvidmxp_WhatsApp%20Image%202026-02-19%20at%2019.46.27.jpeg"
              alt="OPAD Agriculture Programme - Young farmers with seedlings"
              className="w-full rounded-3xl shadow-2xl"
            />
            <p className="text-center text-slate-600 mt-4 italic">Young community members participating in OPAD's seedling programme</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#4A7C59]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/90 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                At the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong>, our Agriculture and Rural Development programmes are designed to strengthen livelihoods, enhance food security, and promote sustainable economic growth within underserved and rural communities.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Recognizing that agriculture remains the primary source of income and survival for millions of vulnerable households globally, OPAD works collaboratively with local farmers, cooperatives, and community-based organizations to implement <strong className="text-[#E5B75F]">climate-smart agricultural practices</strong>, improve productivity, and increase resilience against environmental and economic shocks.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Through targeted interventions such as skills training, access to modern farming techniques, resource-efficient technologies, and market linkages, we empower rural populations to achieve sustainable livelihoods while reducing poverty and food insecurity.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Our initiatives also focus on strengthening value chains, supporting women and youth in agribusiness, and encouraging environmentally responsible land management practices that preserve natural resources for future generations.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg">
                By integrating innovation, local knowledge, and strategic partnerships, OPAD promotes inclusive rural development that not only improves agricultural output but also fosters long-term community resilience, self-reliance, and social well-being. Our work contributes directly to advancing global sustainable development priorities by ensuring equitable access to opportunities in food production, economic participation, and rural enterprise development.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#E5B75F]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#4A7C59]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">What We Do</h2>
            <div className="w-24 h-1 bg-[#4A7C59] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#4A7C59]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-[#4A7C59]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Capacity Building & Skills Development</h3>
              <p className="text-slate-600">Training farmers in climate-smart agriculture and sustainable agribusiness practices.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#E5B75F]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-[#E5B75F]/10 rounded-xl flex items-center justify-center mb-4">
                <Leaf className="w-7 h-7 text-[#E5B75F]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Sustainable Farming & Resource Management</h3>
              <p className="text-slate-600">Promoting efficient resource use and environmentally responsible farming systems.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#7BC4E2]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center mb-4">
                <Mountain className="w-7 h-7 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Market Access & Rural Enterprise Support</h3>
              <p className="text-slate-600">Facilitating market linkages and supporting inclusive rural income-generating initiatives.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Focus Areas */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Strategic Focus Areas
              </h2>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Climate-Smart Agriculture</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E5B75F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Food Security & Nutrition</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Rural Livelihoods & Agribusiness Development</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Women & Youth Empowerment in Agriculture</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Sustainable Land & Water Management</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1500937386664-56d1dfef3854?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Sustainable Agriculture"
                className="rounded-3xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Need Help? Get Involved Today
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Support our agricultural initiatives and help transform rural communities worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E5B75F] hover:bg-[#D4A64E] text-slate-900 rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved">
                <Button className="bg-white hover:bg-slate-100 text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer With Us
                </Button>
              </Link>
              <Link to="/programs">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  View All Programs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
